import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowRight } from "react-icons/fi";
import { Icon } from "@iconify/react";
import { pageTitle } from "../../helpers/PageTitle";
import { blogsData } from "../../data/blogsData";

const categories = [
  "All",
  "Artificial Intelligence",
  "Development",
  "Cloud Solutions",
  "Design",
];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [visibleCount, setVisibleCount] = useState(6);
  pageTitle("Blog");

  const filteredBlogs =
    activeCategory === "All"
      ? blogsData
      : blogsData.filter((b) => b.category === activeCategory);

  const visibleBlogs = filteredBlogs.slice(0, visibleCount);

  return (
    <main className="relative overflow-hidden bg-[#fbfdfd]">
      {/* Background blurs */}
      <div className="pointer-events-none absolute left-[-180px] top-[210px] h-[460px] w-[460px] rounded-full bg-emerald-100/35 blur-3xl" />
      <div className="pointer-events-none absolute right-[-160px] top-[680px] h-[520px] w-[520px] rounded-full bg-teal-100/30 blur-3xl" />

      {/* Hero */}
      <section className="relative z-10 bg-gradient-to-b from-white to-gray-50/60 px-4 pb-[60px] pt-[150px] sm:px-6 max-[991px]:pb-[50px] max-[991px]:pt-[120px]">
        {/* Decorative dots */}
        <div
          className="pointer-events-none absolute left-[5%] top-[35%] h-36 w-52 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(#1b667a 1.5px, transparent 1.5px)",
            backgroundSize: "12px 12px",
          }}
        />
        <div
          className="pointer-events-none absolute right-[5%] top-[45%] h-28 w-40 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(#F5A623 1.5px, transparent 1.5px)",
            backgroundSize: "12px 12px",
          }}
        />
        {/* Decorative circles */}
        <div className="pointer-events-none absolute right-[4%] top-[25%] h-28 w-28 rounded-full border-[3px] border-[#1b667a]/60" />
        <div className="pointer-events-none absolute right-[2%] top-[28%] h-20 w-20 rounded-full border-[3px] border-[#F5A623]/60" />

        <div className="mx-auto max-w-[900px] text-center">
          <div className="mb-5 inline-flex items-center gap-3">
            <div className="h-[2px] w-10 bg-gradient-to-r from-transparent to-[#1b667a]" />
            <span className="text-[11px] font-bold uppercase tracking-[3px] text-[#1b667a]">
              Our Blog
            </span>
            <div className="h-[2px] w-10 bg-gradient-to-l from-transparent to-[#1b667a]" />
          </div>
          <h1 className="text-[clamp(36px,4.5vw,58px)] font-bold leading-[1.12] tracking-[-0.02em] text-gray-900">
            Stay up to date with our{" "}
            <span className="text-[#1b667a]">latest insights</span>
          </h1>
          <p className="mx-auto mt-6 max-w-[560px] text-[16px] leading-[1.8] text-gray-500">
            Expert perspectives on AI, cloud engineering, software development,
            and digital transformation — straight from our engineering team.
          </p>
        </div>
      </section>

      {/* Blog Content */}
      <section className="relative z-10 px-4 sm:px-6 pb-20">
        <div className="mx-auto max-w-[1200px]">
          {/* Category Filter */}
          <div className="mb-10 flex flex-wrap items-center justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setVisibleCount(4);
                }}
                className={`rounded-[15px] px-5 py-2.5 text-[13px] font-semibold transition-all duration-300 ${
                  activeCategory === cat
                    ? "bg-[#1b667a] text-white shadow-lg shadow-[#1b667a]/20"
                    : "border border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Blog Grid */}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visibleBlogs.map((blog, i) => (
              <Link
                key={i}
                to={`/blog/${blog.slug}`}
                className="group overflow-hidden rounded-[15px] border border-gray-100 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.06)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_16px_50px_rgba(0,0,0,0.1)]"
              >
                {/* Image */}
                <div className="relative overflow-hidden">
                  <img
                    src={blog.heroImage}
                    alt={blog.title}
                    className="h-[220px] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-[10px] bg-[#1b667a] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white">
                    {blog.category}
                  </span>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-3 flex items-center gap-4 text-[12px] text-gray-400">
                    <span className="flex items-center gap-1.5">
                      <Icon
                        icon="mdi:calendar-outline"
                        className="h-3.5 w-3.5"
                      />
                      {blog.date}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Icon icon="mdi:clock-outline" className="h-3.5 w-3.5" />
                      {blog.readTime}
                    </span>
                  </div>
                  <h3 className="mb-2.5 text-[19px] font-bold leading-tight text-gray-900 group-hover:text-[#1b667a] transition-colors duration-300">
                    {blog.title}
                  </h3>
                  <p className="mb-4 text-[14px] leading-[1.7] text-gray-500">
                    {blog.excerpt}
                  </p>
                  <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1b667a] group-hover:gap-3 transition-all duration-300">
                    Read More <FiArrowRight className="h-4 w-4" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Load More */}
          {visibleCount < filteredBlogs.length && (
            <div className="mt-12 text-center">
              <button
                onClick={() => setVisibleCount(visibleCount + 3)}
                className="inline-flex items-center gap-2 rounded-[15px] border border-gray-200 bg-white px-8 py-3.5 text-[14px] font-semibold text-gray-700 shadow-sm transition-all duration-300 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
              >
                Load More
                <Icon icon="mdi:chevron-down" className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 px-4 sm:px-6 pb-20">
        <div className="relative mx-auto max-w-[1000px] overflow-hidden rounded-[24px] border border-gray-100 bg-white px-8 py-16 shadow-[0_8px_40px_rgba(0,0,0,0.06)] sm:px-16">
          <div className="pointer-events-none absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-transparent via-[#1b667a]/20 to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 h-full w-1 bg-gradient-to-b from-transparent via-[#F5A623]/20 to-transparent" />
          <div
            className="pointer-events-none absolute left-[3%] top-[8%] h-24 w-32 opacity-[0.08]"
            style={{
              backgroundImage: "radial-gradient(#1b667a 1px, transparent 1px)",
              backgroundSize: "10px 10px",
            }}
          />
          <div
            className="pointer-events-none absolute bottom-[8%] right-[3%] h-20 w-28 opacity-[0.08]"
            style={{
              backgroundImage: "radial-gradient(#F5A623 1px, transparent 1px)",
              backgroundSize: "10px 10px",
            }}
          />
          <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full border border-[#1b667a]/8" />
          <div className="pointer-events-none absolute -bottom-6 -left-6 h-24 w-24 rounded-full border border-[#F5A623]/8" />

          <div className="relative text-center">
            <h2 className="mb-5 text-[clamp(28px,4vw,42px)] font-bold leading-[1.15] text-gray-900">
              Want to stay updated?
            </h2>
            <p className="mx-auto mb-10 max-w-[520px] text-[16px] leading-[1.8] text-gray-500">
              Have a project in mind? Connect with our team to discuss your next
              technology, engineering, or digital transformation initiative.
            </p>
            <div className="mx-auto flex max-w-[460px] justify-center">
              <Link to="/contact" className="rounded-[15px] bg-[#1b667a] px-8 py-3.5 text-[14px] font-semibold text-white shadow-lg shadow-[#1b667a]/20 transition-all duration-300 hover:bg-[#155568] hover:shadow-xl">
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
